import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { api, apiMethods } from '../services/api';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { playSuccessSound, playErrorSound, playWarningSound } from '../utils/sound';

const Login: React.FC = () => {
  const { login, isAuthenticated, setAuthToken, user, updateDOB } = useAuth();
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [tempCredentials, setTempCredentials] = useState<{ mobileNumber: string; password: string } | null>(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [remainingBlockTime, setRemainingBlockTime] = useState(0);
  const [showDOBPrompt, setShowDOBPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Auth state changed, isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      console.log('User is authenticated, navigating to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Check for blocked state and load failed attempts from localStorage
  useEffect(() => {
    const storedFailedAttempts = localStorage.getItem('failedAttempts');
    const storedBlockedUntil = localStorage.getItem('blockedUntil');
    
    if (storedFailedAttempts) {
      setFailedAttempts(parseInt(storedFailedAttempts));
    }
    
    if (storedBlockedUntil) {
      const blockedTime = parseInt(storedBlockedUntil);
      const now = Date.now();
      
      if (now < blockedTime) {
        setBlockedUntil(blockedTime);
        setIsBlocked(true);
        const remaining = Math.ceil((blockedTime - now) / 1000);
        setRemainingBlockTime(remaining);
        
        // Set timer to unblock
        const timeLeft = blockedTime - now;
        setTimeout(() => {
          setIsBlocked(false);
          setBlockedUntil(null);
          setFailedAttempts(0);
          setRemainingBlockTime(0);
          localStorage.removeItem('blockedUntil');
          localStorage.removeItem('failedAttempts');
        }, timeLeft);
      } else {
        // Block time has passed, clear storage
        localStorage.removeItem('blockedUntil');
        localStorage.removeItem('failedAttempts');
      }
    }
  }, []);

  // Update remaining block time every second when blocked
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isBlocked && blockedUntil) {
      // Update immediately
      const remaining = getRemainingBlockTime();
      setRemainingBlockTime(remaining);
      
      interval = setInterval(() => {
        const remaining = getRemainingBlockTime();
        setRemainingBlockTime(remaining);
        
        if (remaining <= 0) {
          setIsBlocked(false);
          setBlockedUntil(null);
          setFailedAttempts(0);
          setRemainingBlockTime(0);
          localStorage.removeItem('blockedUntil');
          localStorage.removeItem('failedAttempts');
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isBlocked, blockedUntil]);

  const playWelcomeSound = () => {
    // Play welcome.mp3 file only
    try {
      const audio = new Audio('/welcome.mp3');
      audio.volume = 0.7; // Set volume to 70%
      audio.preload = 'auto';
      
      // Try to play audio immediately (during user interaction)
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Welcome sound played successfully');
          })
          .catch(err => {
            console.log('Audio playback failed (autoplay blocked):', err);
            // Fallback: Show a toast notification
            toast.success('Welcome! 🎵 (Audio was blocked by browser)');
          });
      }
    } catch (err) {
      console.log('Audio creation failed:', err);
      toast.success('Welcome! 🎵');
    }
  };

  const handleFailedAttempt = () => {
    const newFailedAttempts = failedAttempts + 1;
    setFailedAttempts(newFailedAttempts);
    localStorage.setItem('failedAttempts', newFailedAttempts.toString());
    
    if (newFailedAttempts === 2) {
      toast.error('Warning: One more incorrect attempt will block your account for 5 minutes!');
      playWarningSound();
    } else if (newFailedAttempts >= 3) {
      const blockUntil = Date.now() + 5 * 60 * 1000; // 5 minutes from now
      setBlockedUntil(blockUntil);
      setIsBlocked(true);
      setRemainingBlockTime(5 * 60); // 5 minutes in seconds
      localStorage.setItem('blockedUntil', blockUntil.toString());
      
      toast.error('Account blocked for 5 minutes due to too many failed attempts!');
      playErrorSound();
      
      // Set timer to unblock
      setTimeout(() => {
        setIsBlocked(false);
        setBlockedUntil(null);
        setFailedAttempts(0);
        setRemainingBlockTime(0);
        localStorage.removeItem('blockedUntil');
        localStorage.removeItem('failedAttempts');
      }, 5 * 60 * 1000);
    }
  };

  const resetFailedAttempts = () => {
    setFailedAttempts(0);
    setRemainingBlockTime(0);
    localStorage.removeItem('failedAttempts');
    localStorage.removeItem('blockedUntil');
  };

  const getRemainingBlockTime = () => {
    if (!blockedUntil) return 0;
    const remaining = Math.ceil((blockedUntil - Date.now()) / 1000);
    return Math.max(0, remaining);
  };

  // Remove handleSubmit entirely and merge its logic into handleLogin
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if user is blocked
    if (isBlocked) {
      const minutes = Math.floor(remainingBlockTime / 60);
      const seconds = remainingBlockTime % 60;
      toast.error(`Account is blocked. Please try again in ${minutes}:${seconds.toString().padStart(2, '0')}`);
      return;
    }
    setLoading(true);
    let welcomeAudio: HTMLAudioElement | null = null;
    try {
      welcomeAudio = new Audio('/welcome.mp3');
      welcomeAudio.volume = 0.7;
      welcomeAudio.preload = 'auto';
    } catch (err) {
      console.log('Audio creation failed:', err);
    }
    try {
      if (require2FA) {
        // 2FA step
        await login(tempCredentials!.mobileNumber, tempCredentials!.password, totpCode);
        resetFailedAttempts();
        toast.success('Login successful!');
        setRequire2FA(false);
        setTempCredentials(null);
        setTotpCode('');
        if (welcomeAudio) {
          welcomeAudio.play().catch(() => toast.success('Welcome! 🎵 (Audio was blocked by browser)'));
        }
      } else {
        // Normal login step
        const response = await login(mobileNumber, password, totpCode);
        if (response.require2FA) {
          setRequire2FA(true);
          setTempCredentials({ mobileNumber, password });
          toast.success('Please enter your 2FA code');
        } else if (response.requireDOB) {
          setShowDOBPrompt(true);
        } else if (response.token) {
          resetFailedAttempts();
          toast.success('Login successful!');
          // Play welcome sound for all users on successful login
          playWelcomeSound();
        }
      }
    } catch (error: any) {
      setError(error.message || 'Login failed. Please check your credentials.');
      playErrorSound();
      // 2FA error handling
      if (error.details?.message === '2FA code required') {
        setRequire2FA(true);
        setTempCredentials({ mobileNumber, password });
        toast.success('Please enter your 2FA code');
      } else if (error.details?.message === 'Invalid 2FA code') {
        toast.error('Invalid 2FA code. Please try again.');
        setTotpCode('');
      } else if (error.details?.message === 'Invalid credentials') {
        if (require2FA) {
          toast.error('Invalid 2FA code. Please try again.');
          setTotpCode('');
        } else {
          toast.error('Invalid credentials');
          handleFailedAttempt();
        }
      } else {
        if (require2FA) {
          toast.error('Invalid 2FA code. Please try again.');
          setTotpCode('');
        } else {
          toast.error('Login failed. Please try again.');
          handleFailedAttempt();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPassword = () => {
    setRequire2FA(false);
    setTempCredentials(null);
    setTotpCode('');
  };

  const requestOtp = async (mobileNumber: string) => {
    try {
      await apiMethods.post('/staff/request-otp', { mobileNumber });
    } catch (err: any) {
      throw new Error(err.message || 'Failed to request OTP');
    }
  };

  const verifyOtp = async (mobileNumber: string, otp: string) => {
    try {
      await apiMethods.post('/staff/verify-otp', { mobileNumber, otp });
    } catch (err: any) {
      throw new Error(err.message || 'OTP verification failed');
    }
  };

  const handleRequestOtp = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number.');
      playErrorSound();
      return;
    }
    setError('');
    try {
      await requestOtp(mobileNumber);
      toast.success('OTP sent to your mobile number!');
      playSuccessSound();
      setShowOtpInput(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
      playErrorSound();
    }
  };

  const handleVerifyOtp = async () => {
    try {
      await verifyOtp(mobileNumber, totpCode);
      toast.success('2FA enabled successfully!');
      playSuccessSound();
    } catch (err: any) {
      toast.error(err.message || 'Invalid OTP. Please try again.');
      playErrorSound();
    }
  };

  // Handle DOB submission
  const handleDOBSubmit = async (dob: string) => {
    try {
      await updateDOB(dob);
      setShowDOBPrompt(false);
      toast.success('Date of birth saved!');
      // Optionally, reload page or user context if needed
    } catch (err: any) {
      toast.error(err.message || 'Failed to save date of birth');
    }
  };

  useEffect(() => {
    if (isAuthenticated && !showDOBPrompt) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, showDOBPrompt, navigate]);

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gray-50">
      {/* App Name at Top Center */}
      <div className="w-full py-4 flex justify-center items-center bg-white shadow-sm">
        <h1 className="text-3xl font-extrabold text-gray-900 text-center">Back Office Management</h1>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 items-center justify-center w-full px-4">
        <div className="flex flex-col md:flex-row w-full max-w-5xl bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Banner Image */}
          <div className="md:w-2/3 flex items-center justify-center bg-blue-100 p-8">
            <img
              src="/banner.jpg"
              alt="Help Desk Banner"
              className="rounded-lg shadow-md max-w-full max-h-full"
            />
          </div>
          {/* Login Form */}
          <div className="md:w-1/3 flex items-center justify-center p-8">
            <div className="w-full max-w-md space-y-8">
              {/* Logo above Sign In */}
              <div className="flex flex-col items-center">
                <img src="/logo.png" alt="Company Logo" className="h-16 w-auto mb-4" />
              </div>
              <h2 className="text-center text-2xl font-bold text-gray-900 mb-6">
                {require2FA ? 'Enter 2FA Code' : 'Sign in to your account'}
              </h2>
              {/* Warning messages */}
              {failedAttempts > 0 && failedAttempts < 3 && (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                  <p className="text-sm">
                    {failedAttempts === 1 ? 'Invalid credentials. 2 more attempts remaining.' : 
                     failedAttempts === 2 ? 'Warning: 1 more incorrect attempt will block your account for 5 minutes!' : ''}
                  </p>
                </div>
              )}
              {isBlocked && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p className="text-sm">
                    Account blocked for {Math.floor(remainingBlockTime / 60)}:{(remainingBlockTime % 60).toString().padStart(2, '0')} minutes
                  </p>
                </div>
              )}
              <form className="space-y-6" onSubmit={handleLogin}>
                <div className="space-y-4">
                  {!require2FA ? (
                    <>
                      <div>
                        <label htmlFor="mobile-number" className="sr-only">
                          Mobile Number
                        </label>
                        <input
                          id="mobile-number"
                          name="mobileNumber"
                          type="tel"
                          required
                          className="input-field w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Mobile Number"
                          value={mobileNumber}
                          onChange={(e) => setMobileNumber(e.target.value)}
                          pattern="[0-9]{10}"
                          maxLength={10}
                        />
                      </div>
                      <div>
                        <label htmlFor="password" className="sr-only">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            autoComplete="current-password"
                            className="input-field w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-center mb-4">
                        <div className="text-sm text-gray-600 mb-2">
                          Enter the 6-digit code from your Google Authenticator app
                        </div>
                        <div className="text-xs text-gray-500">
                          Mobile: {tempCredentials?.mobileNumber}
                        </div>
                      </div>
                      <div>
                        <label htmlFor="totp-code" className="sr-only">
                          2FA Code
                        </label>
                        <input
                          id="totp-code"
                          name="totpCode"
                          type="text"
                          required
                          className="input-field w-full rounded-md border border-gray-300 px-3 py-2 text-center text-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="000000"
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          pattern="[0-9]{6}"
                          maxLength={6}
                          autoFocus
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading || isBlocked}
                    className={`btn-primary w-full ${isBlocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold py-2 px-4 rounded-md transition duration-200`}
                  >
                    {loading ? 'Signing in...' : isBlocked ? 'Account Blocked' : require2FA ? 'Verify & Sign in' : 'Sign in'}
                  </button>
                  {require2FA && (
                    <button
                      type="button"
                      onClick={handleBackToPassword}
                      className="w-full text-gray-600 hover:text-gray-800 text-sm py-2"
                    >
                      ← Back to password
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info at Bottom */}
      <div className="w-full bg-gray-100 py-3 flex flex-col items-center text-gray-600 text-sm">
        <div className="mb-1 text-center">
          <span className="font-semibold">Contact:</span> 9721-337-337, 9721-338-338 &nbsp;|&nbsp; 
          <span className="font-semibold">Email:</span> <a href="mailto:support@mydigicash.in" className="underline">support@mydigicash.in</a> &nbsp;|&nbsp; 
          <span className="font-semibold">Website:</span> <a href="https://mydigicash.in" className="underline" target="_blank" rel="noopener noreferrer">mydigicash.in</a>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-gray-100 py-2">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-center text-gray-600 text-xs">
          &copy; 2025 DigiCASH INDIA
        </div>
      </footer>
      {/* DOB Prompt Modal */}
      {showDOBPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-xs flex flex-col items-center">
            <h2 className="text-lg font-bold mb-4">Enter Your Date of Birth</h2>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Please provide your date of birth to continue
            </p>
            <input
              type="date"
              className="border rounded px-3 py-2 mb-4 w-full"
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                if (e.target.value) {
                  handleDOBSubmit(e.target.value);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;